/**
 * RSS-355 — carries per-location stock into checkout for the in-store pickup function.
 * The answer must be on the cart before checkout starts. docs/PickupStock.md
 */
document.addEventListener('DOMContentLoaded', () => {
    const ATTRIBUTE = '_pickup_stock';

    // Sibling attribute, not a wrapper. docs/PickupStock.md -> "The freshness stamp".
    const DATE_ATTRIBUTE = '_pickup_stock_date';
    const DATE_KEY = '$date';

    const ENDPOINT = '/apps/rss-app/pickup-stock';

    // Oversized input makes the function FAIL OPEN. docs/PickupStock.md -> "The size guard".
    const MAX_ATTRIBUTE_BYTES = 60000;

    // Keeps us out of the 429 cascade. docs/CartRateLimiting.md
    const DEBOUNCE_MS = 800;
    const INITIAL_DELAY_MS = 2500;
    const RETRY_AFTER_429_MS = 1500;

    // Checkout must never wait on us.
    const CHECKOUT_TIMEOUT_MS = 1200;

    let timer = null;
    let inFlight = false;
    let pendingCart = null;

    const debug = (...args) => {
        if (window.__pickupStockDebug) console.log('[pickup-stock]', ...args);
    };
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const variantIdsOf = (cart) => [
        ...new Set((cart?.items || []).map((item) => String(item.variant_id)).filter(Boolean)),
    ];

    async function readCart() {
        try {
            const response = await fetch(window.Shopify.routes.root + 'cart.js');
            return response.ok ? await response.json() : null;
        } catch {
            return null;
        }
    }

    /** Both attributes always move together: a map without its stamp is refused by the function. */
    async function writeAttributes(value, date) {
        for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
                const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        attributes: { [ATTRIBUTE]: value, [DATE_ATTRIBUTE]: date },
                    }),
                });
                if (response.status !== 429) {
                    debug('wrote', value.length, 'bytes, dated', date || '(none)');
                    return;
                }
                debug('rate limited, retrying');
                await sleep(RETRY_AFTER_429_MS);
            } catch (error) {
                debug('write failed', error);
                return;
            }
        }
    }

    /** Fetches stock for the cart and stores it. Any failure clears the attributes -- fail closed. */
    async function refresh(cart, signal) {
        const stored = cart?.attributes?.[ATTRIBUTE];
        const storedDate = cart?.attributes?.[DATE_ATTRIBUTE];
        const anythingStored = Boolean(stored || storedDate);
        const variants = variantIdsOf(cart);
        if (variants.length === 0) {
            if (anythingStored) await writeAttributes('', '');
            return;
        }

        let body;
        try {
            const response = await fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ variants }),
                signal,
            });
            if (!response.ok) throw new Error(String(response.status));
            body = await response.json();
        } catch (error) {
            debug('lookup failed, clearing', error);
            if (anythingStored) await writeAttributes('', '');
            return;
        }

        // The endpoint returns its error envelope with HTTP 200, so `response.ok` is not enough.
        if (!body || typeof body !== 'object' || Array.isArray(body) || body.error === true) {
            debug('unusable response, clearing', body);
            if (anythingStored) await writeAttributes('', '');
            return;
        }

        // The stamp rides in the body, not a header. docs/PickupStock.md -> "The freshness stamp".
        const date = typeof body?.[DATE_KEY] === 'string' ? body[DATE_KEY] : '';
        const stock = {};
        Object.keys(body || {}).forEach((key) => {
            if (key !== DATE_KEY) stock[key] = body[key];
        });

        const value = JSON.stringify(stock);
        if (value.length > MAX_ATTRIBUTE_BYTES) {
            console.warn(
                `[pickup-stock] ${value.length} bytes exceeds the ${MAX_ATTRIBUTE_BYTES} limit; ` +
                'pickup will not be offered for this cart'
            );
            if (anythingStored) await writeAttributes('', '');
            return;
        }

        if (stored === value && storedDate === date) {
            debug('unchanged, skipping write');
            return;
        }
        await writeAttributes(value, date);
    }

    /** Coalesces bursts into one refresh, so a single add-to-cart costs one cart write. */
    function schedule(cart, delay = DEBOUNCE_MS) {
        if (cart) pendingCart = cart;
        clearTimeout(timer);
        timer = setTimeout(async () => {
            if (inFlight) {
                schedule(null);
                return;
            }
            inFlight = true;
            try {
                const target = pendingCart || (await readCart());
                pendingCart = null;
                if (target) await refresh(target);
            } finally {
                inFlight = false;
            }
        }, delay);
    }

    // Only a new variant skips the debounce. docs/PickupStock.md -> "Which changes are urgent".
    const onCartMutation = (event) => {
        const cart = event.detail?.cart;
        schedule(cart, cart && gainsUnmappedVariant(cart) ? 0 : DEBOUNCE_MS);
    };
    document.addEventListener('on:cart:add', onCartMutation);
    document.addEventListener('on:line-item:change', onCartMutation);

    // Opportunistic last refresh -- a race against page unload, not a guarantee.
    document.addEventListener(
        'click',
        (event) => {
            if (!event.target?.closest?.('[name="checkout"]')) return;
            clearTimeout(timer);
            const controller = new AbortController();
            setTimeout(() => controller.abort(), CHECKOUT_TIMEOUT_MS);
            readCart().then((cart) => cart && refresh(cart, controller.signal));
        },
        true
    );

    const pad = (value) => String(value).padStart(2, '0');

    /**
     * The two dates the shop's own date could be from here — a stored date outside the pair is from
     * an earlier day. docs/PickupStock.md -> "The freshness stamp".
     */
    function plausibleToday() {
        const now = new Date();
        return [
            `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
            now.toISOString().slice(0, 10),
        ];
    }

    /** The stored map, or null when there is none or it cannot be read. */
    function storedMap(cart) {
        const stored = cart?.attributes?.[ATTRIBUTE];
        if (!stored) return null;
        let parsed;
        try {
            parsed = JSON.parse(stored);
        } catch {
            return null;
        }
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
        return parsed;
    }

    /**
     * True when the cart holds a variant the map says nothing about — the only change that can make
     * the map wrong. docs/PickupStock.md -> "Which changes are urgent".
     */
    function gainsUnmappedVariant(cart) {
        const variants = variantIdsOf(cart);
        if (variants.length === 0) return false;
        const parsed = storedMap(cart);
        if (!parsed) return true;
        return variants.some((id) => !Object.prototype.hasOwnProperty.call(parsed, id));
    }

    /**
     * True when the stored value does not describe this cart, or predates today. Compares key sets,
     * not stock -- "nowhere" is a real answer. docs/PickupStock.md -> "The freshness stamp".
     */
    function isStale(cart) {
        const storedDate = cart?.attributes?.[DATE_ATTRIBUTE];
        const variants = variantIdsOf(cart);
        if (variants.length === 0) return Boolean(cart?.attributes?.[ATTRIBUTE] || storedDate);
        const parsed = storedMap(cart);
        if (!parsed) return true;
        // A missing stamp is stale too — unknown freshness is not freshness.
        if (!plausibleToday().includes(storedDate)) return true;
        if (Object.keys(parsed).length !== variants.length) return true;
        return gainsUnmappedVariant(cart);
    }

    /** Re-reads the cart and refreshes only if the stored map has drifted from it. */
    function verify() {
        readCart().then((cart) => {
            if (cart && isStale(cart)) schedule(cart, 0);
        });
    }

    // Catch an add whose event was lost to the drawer re-render, and a bfcache restore that never
    // re-fires DOMContentLoaded. docs/PickupStock.md -> "Why the drawer and bfcache hooks exist".
    document.addEventListener('on:cart-drawer:before-open', verify);
    document.addEventListener('on:bfcache:load-restore', verify);

    // Catches a cart changed in another tab, or by a path that dispatches no event. Deferred so it
    // never joins the add-to-cart cascade.
    setTimeout(verify, INITIAL_DELAY_MS);
});
